/**
 * Pure-function pricing engine. Given a list of course-objects in the cart and
 * the list of active promotions, returns a breakdown:
 *
 *   {
 *     subtotal,            // sum of base prices
 *     discountTotal,       // total saved
 *     total,               // final amount to pay
 *     lines: [{course, basePrice, discount, finalPrice, appliedPromos:[...]}],
 *     appliedPackages: [{promo, courses, fixed_price, originalSum, savings}],
 *     bulkApplied,         // {promo, percent, base, amount} or null
 *   }
 *
 * Stacking strategy (applied in this exact order):
 *   1) Packages: a package applies when ALL of its course_ids are present in
 *      the cart. The courses absorbed by the package are billed at the fixed
 *      price (no other discount applies to them). If multiple packages overlap,
 *      we apply packages greedily in order of biggest savings first, then
 *      remove their courses from the pool.
 *   2) Course discounts: for each loose course (not absorbed by any package),
 *      pick the largest applicable course discount (percent or fixed €).
 *   3) Bulk discount: among bulk promotions whose min_courses ≤ number of loose
 *      courses, pick the highest percent and apply to the loose subtotal.
 */

const courseSavings = (price, promo) => {
  if (!promo || promo.kind !== "course") return 0;
  if (promo.course_discount_type === "percent") {
    return (price * (promo.course_discount_value || 0)) / 100;
  }
  return Math.min(price, promo.course_discount_value || 0);
};

export function computeCart(courses, promotions) {
  const subtotal = courses.reduce((acc, c) => acc + (Number(c.price) || 0), 0);

  // ---- 1) Packages (greedy by savings) ----
  const idSet = new Set(courses.map((c) => c.id));
  const idToCourse = Object.fromEntries(courses.map((c) => [c.id, c]));

  const eligiblePackages = (promotions || [])
    .filter((p) => p.kind === "package" && Array.isArray(p.course_ids) && p.course_ids.length >= 2)
    .filter((p) => p.course_ids.every((cid) => idSet.has(cid)))
    .map((p) => {
      const originalSum = p.course_ids.reduce(
        (acc, cid) => acc + (Number(idToCourse[cid]?.price) || 0),
        0,
      );
      return { promo: p, originalSum, savings: Math.max(0, originalSum - (p.fixed_price || 0)) };
    })
    .sort((a, b) => b.savings - a.savings);

  const absorbed = new Set();
  const appliedPackages = [];
  for (const pkg of eligiblePackages) {
    if (pkg.promo.course_ids.some((cid) => absorbed.has(cid))) continue;
    pkg.promo.course_ids.forEach((cid) => absorbed.add(cid));
    appliedPackages.push({
      promo: pkg.promo,
      courses: pkg.promo.course_ids.map((cid) => idToCourse[cid]),
      fixed_price: pkg.promo.fixed_price || 0,
      originalSum: pkg.originalSum,
      savings: pkg.savings,
    });
  }

  // ---- 2) Per-course discounts on the loose pool ----
  const looseCourses = courses.filter((c) => !absorbed.has(c.id));
  const coursePromosById = {};
  for (const p of promotions || []) {
    if (p.kind !== "course" || !p.course_id) continue;
    const existing = coursePromosById[p.course_id];
    const newSav = courseSavings(idToCourse[p.course_id]?.price || 0, p);
    const oldSav = existing ? courseSavings(idToCourse[p.course_id]?.price || 0, existing) : -1;
    if (newSav > oldSav) coursePromosById[p.course_id] = p;
  }

  const lines = looseCourses.map((c) => {
    const promo = coursePromosById[c.id];
    const basePrice = Number(c.price) || 0;
    const discount = promo ? courseSavings(basePrice, promo) : 0;
    return {
      course: c,
      basePrice,
      discount,
      finalPrice: Math.max(0, basePrice - discount),
      appliedPromos: promo ? [promo] : [],
    };
  });

  // ---- 3) Bulk discount on remaining loose subtotal ----
  const looseSubtotalAfterCoursePromos = lines.reduce((acc, l) => acc + l.finalPrice, 0);
  const bulkCandidates = (promotions || []).filter(
    (p) => p.kind === "bulk" && (p.bulk_percent || 0) > 0 && lines.length >= (p.bulk_min_courses || 2),
  );
  bulkCandidates.sort((a, b) => (b.bulk_percent || 0) - (a.bulk_percent || 0));
  const bulkPromo = bulkCandidates[0] || null;
  let bulkApplied = null;
  if (bulkPromo) {
    const amount = (looseSubtotalAfterCoursePromos * (bulkPromo.bulk_percent || 0)) / 100;
    bulkApplied = {
      promo: bulkPromo,
      percent: bulkPromo.bulk_percent,
      base: looseSubtotalAfterCoursePromos,
      amount,
    };
  }

  const packageTotal = appliedPackages.reduce((acc, p) => acc + p.fixed_price, 0);
  const looseAfterBulk = looseSubtotalAfterCoursePromos - (bulkApplied?.amount || 0);
  const total = packageTotal + looseAfterBulk;

  return {
    subtotal,
    discountTotal: subtotal - total,
    total,
    lines,
    appliedPackages,
    bulkApplied,
  };
}
